import type {
    CatalogBrandCreateInput,
    CatalogBrandListQuery,
    CatalogBrandUpdateInput,
    CatalogFitmentCreateInput,
    CatalogProductCreateInput,
    CatalogProductListQuery,
    CatalogProductUpdateInput,
    CatalogSkuAlternateCreateInput,
    CatalogSkuCreateInput,
    CatalogSkuListQuery,
    CatalogSkuLookupListQuery,
    CatalogSkuSpecCreateInput,
    CatalogSkuSupersessionCreateInput,
    CatalogSkuUpdateInput,
    EpcDiagramCreateInput,
    EpcDiagramPartCreateInput,
    EpcDiagramPartUpdateInput,
    EpcDiagramUpdateInput,
    EpcGroupCreateInput,
    EpcGroupUpdateInput,
    VehicleChassisRangeCreateInput,
    VehicleChassisRangeUpdateInput,
    VehicleConfigurationCodeAssignmentsSetInput,
    VehicleConfigurationCreateInput,
    VehicleConfigurationListQuery,
    VehicleConfigurationUpdateInput,
    VehicleMakeCreateInput,
    VehicleMakeListQuery,
    VehicleMakeUpdateInput,
    VehicleModelCodeCreateInput,
    VehicleModelCodeUpdateInput,
    VehicleModelCreateInput,
    VehicleModelListQuery,
    VehicleModelUpdateInput,
} from "@/modules/catalog/domain/contracts";
import { config } from "@/shared/lib/config";
import { fetcher } from "@/shared/lib/fetcher";

export type CatalogProductListItem = {
  id: string;
  name: string;
  type: string;
  status: string;
  categoryId: string | null;
  brandId: string | null;
  categoryName: string | null;
  brandName: string | null;
  thumbnailUrl: string | null;
  updatedAt: string;
};

export type CatalogProductListResult = {
  data: CatalogProductListItem[];
  meta: { total: number; limit: number; offset: number };
};

export type CatalogProductDetail = {
  id: string;
  categoryId: string | null;
  brandId: string | null;
  type: string;
  name: string;
  description: string | null;
  status: string;
  createdAt: string;
  updatedAt: string;
  categoryName: string | null;
  brandName: string | null;
};

export type CatalogProductMergePreview = {
  sourceProductId: string;
  targetProductId: string;
  sourceSkuCount: number;
  targetSkuCount: number;
  shopOfferCount: number;
  fitmentCount: number;
};

export type CatalogProductMergeResult = {
  targetProductId: string;
  mergedSkuCount: number;
  movedSkuCount: number;
  shopOfferCount: number;
};

export type SkuMediaItem = {
  id: string;
  url: string;
  tag: string | null;
  sortOrder: number | null;
  createdAt: string;
};

export type CatalogSkuDTO = {
  id: string;
  catalogProductId: string;
  oemPartNo: string | null;
  condition: string | null;
  thumbnailUrl: string | null;
  createdAt: string;
  updatedAt: string;
};

export type CatalogSkuListResult = {
  data: CatalogSkuDTO[];
  meta: { total: number; limit: number; offset: number };
};

export type CatalogSkuLookupItem = {
  id: string;
  catalogProductId: string;
  productName: string;
  oemPartNo: string | null;
  condition: string | null;
  thumbnailUrl: string | null;
};

export type CatalogSkuLookupListResult = {
  data: CatalogSkuLookupItem[];
  meta: { total: number; limit: number; offset: number };
};

export type CatalogFitmentDTO = {
  id: string;
  catalogSkuId: string;
  vehicleConfigurationId: string;
  fitmentNote: string | null;
  quantityPerVehicle: number | null;
  applicabilityCode: string | null;
  createdAt: string;
  makeName: string;
  modelName: string;
  configurationLabel: string;
};

export type BrandDTO = {
  id: string;
  name: string;
  createdAt: string;
};

export type BrandListResult = {
  data: BrandDTO[];
  meta: { total: number; limit: number; offset: number };
};

export type VehicleMakeDTO = {
  id: string;
  name: string;
  imageUrl: string | null;
};

export type VehicleMakeListResult = {
  data: VehicleMakeDTO[];
  meta: { total: number; limit: number; offset: number };
};

export type VehicleModelDTO = {
  id: string;
  makeId: string;
  makeName: string;
  name: string;
};

export type VehicleModelListResult = {
  data: VehicleModelDTO[];
  meta: { total: number; limit: number; offset: number };
};

export type VehicleConfigurationListResult = {
  data: VehicleConfigurationLookupItem[];
  meta: { total: number; limit: number; offset: number };
};

export type VehicleConfigurationLookupItem = {
  id: string;
  makeId: string;
  modelId: string;
  label: string;
  makeName: string;
  modelName: string;
  modelCode: string | null;
  yearFrom: number | null;
  yearTo: number | null;
  monthFrom: number | null;
  monthTo: number | null;
  engine: string | null;
  marketCode: string | null;
  transmissionCode: string | null;
  engineCode: string | null;
  gradeCode: string | null;
  adminNotes: string | null;
  sortOrder: number;
};

export type VehicleModelCodeDTO = {
  id: string;
  modelId: string;
  codeType: "spec" | "option";
  code: string;
  label: string | null;
  notes: string | null;
  sortOrder: number;
};

/** @deprecated Use VehicleModelCodeDTO */
export type VehicleConfigurationCodeDTO = VehicleModelCodeDTO;

export type VehicleConfigurationCodeAssignmentsDTO = {
  modelCodeIds: string[];
  assigned: VehicleModelCodeDTO[];
  modelCodes: VehicleModelCodeDTO[];
};

export type VehicleConfigurationDetailDTO = {
  id: string;
  modelId: string;
  makeId: string;
  makeName: string;
  modelName: string;
  modelCode: string | null;
  yearFrom: number | null;
  yearTo: number | null;
  monthFrom: number | null;
  monthTo: number | null;
  engine: string | null;
  marketCode: string | null;
  transmissionCode: string | null;
  engineCode: string | null;
  gradeCode: string | null;
  adminNotes: string | null;
  sortOrder: number;
  codes: VehicleModelCodeDTO[];
  chassisRangeCount: number;
  epcGroupCount: number;
};

export type VehicleChassisRangeDTO = {
  id: string;
  vehicleConfigurationId: string;
  vinPrefix: string | null;
  chassisPrefix: string | null;
  chassisFrom: string | null;
  chassisTo: string | null;
  productionFrom: string | null;
  productionTo: string | null;
  notes: string | null;
  createdAt: string;
};

export type CatalogSkuSupersessionDTO = {
  id: string;
  fromPartNo: string;
  toPartNo: string;
  toCatalogSkuId: string | null;
  supersededAt: string | null;
  notes: string | null;
  createdAt: string;
};

export type CatalogSkuAlternateDTO = {
  id: string;
  catalogSkuId: string;
  altPartNo: string;
  altBrand: string | null;
  altType: string | null;
  notes: string | null;
  createdAt: string;
};

export type CatalogSkuSpecDTO = {
  id: string;
  catalogSkuId: string;
  specKey: string;
  specValue: string;
  specUnit: string | null;
  createdAt: string;
};

export type EpcGroupDTO = {
  id: string;
  vehicleConfigurationId: string;
  parentGroupId: string | null;
  groupCode: string | null;
  groupName: string;
  sortOrder: number;
  createdAt: string;
};

export type EpcDiagramDTO = {
  id: string;
  epcGroupId: string;
  title: string | null;
  imageUrl: string;
  sortOrder: number;
  createdAt: string;
};

export type EpcDiagramPartDTO = {
  id: string;
  epcDiagramId: string;
  catalogProductId: string;
  productName?: string;
  thumbnailUrl?: string | null;
  calloutNumber: string;
  xPct: number;
  yPct: number;
  widthPct: number | null;
  heightPct: number | null;
  quantityPerVehicle: number | null;
  applicabilityNote: string | null;
  catalogSkuIds?: string[];
  skus?: Array<{
    catalogSkuId: string;
    oemPartNo: string | null;
    condition: string | null;
  }>;
  createdAt: string;
};

export type EpcFitmentProductListItem = CatalogProductListItem & {
  skuCount: number;
};

export type EpcFitmentSkuListItem = {
  id: string;
  catalogProductId: string;
  oemPartNo: string | null;
  condition: string | null;
  fitmentNote: string | null;
  quantityPerVehicle: number | null;
};

export type VehicleImportRowResult = {
  sheet:
    | "configurations"
    | "model_codes"
    | "code_assignments"
    | "chassis_ranges";
  rowNumber: number;
  action: "create" | "update" | "skip" | "error";
  message?: string;
  keys?: {
    makeName?: string;
    modelName?: string;
    configurationLabel?: string;
  };
};

export type VehicleImportResult = {
  ok: boolean;
  summary: {
    creates: number;
    updates: number;
    skips: number;
    errors: number;
  };
  rows: VehicleImportRowResult[];
};

const base = () => `${config.apiUrl}/catalog`;

export const catalogApi = {
  // Products
  listProducts(query: CatalogProductListQuery) {
    const params = new URLSearchParams();
    params.set("limit", String(query.limit ?? 20));
    params.set("offset", String(query.offset ?? 0));
    if (query.status) params.set("status", query.status);
    if (query.categoryId) params.set("categoryId", query.categoryId);
    if (query.brandId) params.set("brandId", query.brandId);
    if (query.type) params.set("type", query.type);
    if (query.search) params.set("search", query.search);
    return fetcher.get<CatalogProductListResult>(
      `${base()}/products?${params.toString()}`,
    );
  },

  getProduct(id: string) {
    return fetcher.get<CatalogProductDetail>(`${base()}/products/${id}`);
  },

  createProduct(input: CatalogProductCreateInput) {
    return fetcher.post<CatalogProductDetail>(`${base()}/products`, input);
  },

  updateProduct(id: string, input: CatalogProductUpdateInput) {
    return fetcher.put<CatalogProductDetail>(`${base()}/products/${id}`, input);
  },

  deleteProduct(id: string) {
    return fetcher.delete<{ ok: boolean }>(`${base()}/products/${id}`);
  },

  previewProductMerge(query: {
    sourceProductId: string;
    targetProductId: string;
  }) {
    const params = new URLSearchParams({
      sourceProductId: query.sourceProductId,
      targetProductId: query.targetProductId,
    });
    return fetcher.get<CatalogProductMergePreview>(
      `${base()}/products/merge/preview?${params.toString()}`,
    );
  },

  mergeProducts(input: {
    sourceProductId: string;
    targetProductId: string;
  }) {
    return fetcher.post<CatalogProductMergeResult>(
      `${base()}/products/merge`,
      input,
    );
  },

  listSkuMedia(skuId: string) {
    return fetcher.get<SkuMediaItem[]>(`${base()}/skus/${skuId}/media`);
  },

  addSkuMedia(skuId: string, url: string) {
    return fetcher.post<{ id: string }>(`${base()}/skus/${skuId}/media`, {
      url,
    });
  },

  deleteSkuMedia(skuId: string, mediaId: string) {
    return fetcher.delete<{ ok: boolean }>(
      `${base()}/skus/${skuId}/media/${mediaId}`,
    );
  },

  reorderSkuMedia(skuId: string, mediaIds: string[]) {
    return fetcher.put<{ ok: boolean }>(
      `${base()}/skus/${skuId}/media/reorder`,
      { mediaIds },
    );
  },

  // SKUs
  listSkus(productId: string, query: Partial<CatalogSkuListQuery> = {}) {
    const params = new URLSearchParams();
    params.set("limit", String(query.limit ?? 20));
    params.set("offset", String(query.offset ?? 0));
    if (query.search) params.set("search", query.search);
    if (query.condition) params.set("condition", query.condition);
    return fetcher.get<CatalogSkuListResult>(
      `${base()}/products/${productId}/skus?${params.toString()}`,
    );
  },

  createSku(
    productId: string,
    input: Omit<CatalogSkuCreateInput, "catalogProductId">,
  ) {
    return fetcher.post<CatalogSkuDTO>(
      `${base()}/products/${productId}/skus`,
      input,
    );
  },

  updateSku(id: string, input: CatalogSkuUpdateInput) {
    return fetcher.put<CatalogSkuDTO>(`${base()}/skus/${id}`, input);
  },

  deleteSku(id: string) {
    return fetcher.delete<{ ok: boolean }>(`${base()}/skus/${id}`);
  },

  lookupCatalogSkus(query: CatalogSkuLookupListQuery) {
    const params = new URLSearchParams();
    params.set("limit", String(query.limit ?? 20));
    params.set("offset", String(query.offset ?? 0));
    if (query.search) params.set("search", query.search);
    return fetcher.get<CatalogSkuLookupListResult>(
      `${base()}/skus/lookup?${params.toString()}`,
    );
  },

  async getSkuLookup(id: string): Promise<CatalogSkuLookupItem | null> {
    try {
      return await fetcher.get<CatalogSkuLookupItem>(`${base()}/skus/${id}`);
    } catch {
      return null;
    }
  },

  // Fitments
  listFitments(skuId: string) {
    return fetcher.get<CatalogFitmentDTO[]>(`${base()}/skus/${skuId}/fitments`);
  },

  createFitment(
    skuId: string,
    input: Omit<CatalogFitmentCreateInput, "catalogSkuId">,
  ) {
    return fetcher.post<CatalogFitmentDTO>(
      `${base()}/skus/${skuId}/fitments`,
      input,
    );
  },

  deleteFitment(id: string) {
    return fetcher.delete<{ ok: boolean }>(`${base()}/fitments/${id}`);
  },

  // Lookups
  async getBrand(id: string): Promise<BrandDTO | null> {
    try {
      return await fetcher.get<BrandDTO>(`${base()}/brands/${id}`);
    } catch {
      return null;
    }
  },

  listBrands(query: Partial<CatalogBrandListQuery> = {}) {
    const params = new URLSearchParams();
    params.set("limit", String(query.limit ?? 20));
    params.set("offset", String(query.offset ?? 0));
    if (query.search) params.set("search", query.search);
    return fetcher.get<BrandListResult>(
      `${base()}/brands?${params.toString()}`,
    );
  },

  createBrand(input: CatalogBrandCreateInput) {
    return fetcher.post<BrandDTO>(`${base()}/brands`, input);
  },

  updateBrand(id: string, input: CatalogBrandUpdateInput) {
    return fetcher.put<BrandDTO>(`${base()}/brands/${id}`, input);
  },

  deleteBrand(id: string) {
    return fetcher.delete<{ ok: boolean }>(`${base()}/brands/${id}`);
  },

  listVehicleMakes(query: VehicleMakeListQuery) {
    const params = new URLSearchParams();
    params.set("limit", String(query.limit ?? 20));
    params.set("offset", String(query.offset ?? 0));
    if (query.search) params.set("search", query.search);
    return fetcher.get<VehicleMakeListResult>(
      `${base()}/vehicle-makes?${params.toString()}`,
    );
  },

  getVehicleMake(id: string) {
    return fetcher.get<VehicleMakeDTO>(`${base()}/vehicle-makes/${id}`);
  },

  createVehicleMake(input: VehicleMakeCreateInput) {
    return fetcher.post<VehicleMakeDTO>(`${base()}/vehicle-makes`, input);
  },

  updateVehicleMake(id: string, input: VehicleMakeUpdateInput) {
    return fetcher.put<VehicleMakeDTO>(`${base()}/vehicle-makes/${id}`, input);
  },

  deleteVehicleMake(id: string) {
    return fetcher.delete<{ ok: boolean }>(`${base()}/vehicle-makes/${id}`);
  },

  listVehicleModels(query: Partial<VehicleModelListQuery> = {}) {
    const params = new URLSearchParams();
    params.set("limit", String(query.limit ?? 20));
    params.set("offset", String(query.offset ?? 0));
    if (query.makeId) params.set("makeId", query.makeId);
    return fetcher.get<VehicleModelListResult>(
      `${base()}/vehicle-models?${params.toString()}`,
    );
  },

  createVehicleModel(input: VehicleModelCreateInput) {
    return fetcher.post<VehicleModelDTO>(`${base()}/vehicle-models`, input);
  },

  updateVehicleModel(id: string, input: VehicleModelUpdateInput) {
    return fetcher.put<VehicleModelDTO>(
      `${base()}/vehicle-models/${id}`,
      input,
    );
  },

  deleteVehicleModel(id: string) {
    return fetcher.delete<{ ok: boolean }>(`${base()}/vehicle-models/${id}`);
  },

  listVehicleConfigurations(query: Partial<VehicleConfigurationListQuery> = {}) {
    const params = new URLSearchParams();
    params.set("limit", String(query.limit ?? 20));
    params.set("offset", String(query.offset ?? 0));
    if (query.makeId) params.set("makeId", query.makeId);
    if (query.modelId) params.set("modelId", query.modelId);
    return fetcher.get<VehicleConfigurationListResult>(
      `${base()}/vehicle-configurations?${params.toString()}`,
    );
  },

  createVehicleConfiguration(input: VehicleConfigurationCreateInput) {
    return fetcher.post<VehicleConfigurationLookupItem>(
      `${base()}/vehicle-configurations`,
      input,
    );
  },

  updateVehicleConfiguration(
    id: string,
    input: VehicleConfigurationUpdateInput,
  ) {
    return fetcher.put<VehicleConfigurationLookupItem>(
      `${base()}/vehicle-configurations/${id}`,
      input,
    );
  },

  deleteVehicleConfiguration(id: string) {
    return fetcher.delete<{ ok: boolean }>(
      `${base()}/vehicle-configurations/${id}`,
    );
  },

  getVehicleConfigurationDetail(configurationId: string) {
    return fetcher.get<VehicleConfigurationDetailDTO>(
      `${base()}/vehicle-configurations/${configurationId}/detail`,
    );
  },

  listVehicleModelCodes(modelId: string) {
    return fetcher.get<VehicleModelCodeDTO[]>(
      `${base()}/vehicle-models/${modelId}/codes`,
    );
  },

  createVehicleModelCode(
    modelId: string,
    input: Omit<VehicleModelCodeCreateInput, "modelId">,
  ) {
    return fetcher.post<VehicleModelCodeDTO>(
      `${base()}/vehicle-models/${modelId}/codes`,
      input,
    );
  },

  updateVehicleModelCode(id: string, input: VehicleModelCodeUpdateInput) {
    return fetcher.put<VehicleModelCodeDTO>(
      `${base()}/vehicle-model-codes/${id}`,
      input,
    );
  },

  deleteVehicleModelCode(id: string) {
    return fetcher.delete<{ ok: boolean }>(
      `${base()}/vehicle-model-codes/${id}`,
    );
  },

  getConfigurationCodeAssignments(configurationId: string) {
    return fetcher.get<VehicleConfigurationCodeAssignmentsDTO>(
      `${base()}/vehicle-configurations/${configurationId}/code-assignments`,
    );
  },

  setConfigurationCodeAssignments(
    configurationId: string,
    input: VehicleConfigurationCodeAssignmentsSetInput,
  ) {
    return fetcher.put<{
      modelCodeIds: string[];
      assigned: VehicleModelCodeDTO[];
    }>(
      `${base()}/vehicle-configurations/${configurationId}/code-assignments`,
      input,
    );
  },

  /** @deprecated Use listVehicleModelCodes */
  listVehicleConfigurationCodes(configurationId: string) {
    return fetcher.get<VehicleModelCodeDTO[]>(
      `${base()}/vehicle-configurations/${configurationId}/codes`,
    );
  },

  /** @deprecated Use createVehicleModelCode */
  createVehicleConfigurationCode(
    configurationId: string,
    input: Omit<VehicleModelCodeCreateInput, "modelId">,
  ) {
    return fetcher.post<VehicleModelCodeDTO>(
      `${base()}/vehicle-configurations/${configurationId}/codes`,
      input,
    );
  },

  /** @deprecated Use updateVehicleModelCode */
  updateVehicleConfigurationCode(
    id: string,
    input: VehicleModelCodeUpdateInput,
  ) {
    return fetcher.put<VehicleModelCodeDTO>(
      `${base()}/vehicle-configuration-codes/${id}`,
      input,
    );
  },

  /** @deprecated Use deleteVehicleModelCode */
  deleteVehicleConfigurationCode(id: string) {
    return fetcher.delete<{ ok: boolean }>(
      `${base()}/vehicle-configuration-codes/${id}`,
    );
  },

  listChassisRanges(configurationId: string) {
    return fetcher.get<VehicleChassisRangeDTO[]>(
      `${base()}/vehicle-configurations/${configurationId}/chassis-ranges`,
    );
  },

  createVehicleChassisRange(input: VehicleChassisRangeCreateInput) {
    return fetcher.post<VehicleChassisRangeDTO>(
      `${base()}/vehicle-chassis-ranges`,
      input,
    );
  },

  updateVehicleChassisRange(id: string, input: VehicleChassisRangeUpdateInput) {
    return fetcher.put<VehicleChassisRangeDTO>(
      `${base()}/vehicle-chassis-ranges/${id}`,
      input,
    );
  },

  deleteVehicleChassisRange(id: string) {
    return fetcher.delete<{ ok: boolean }>(
      `${base()}/vehicle-chassis-ranges/${id}`,
    );
  },

  listSkuSupersessions(skuId: string) {
    return fetcher.get<CatalogSkuSupersessionDTO[]>(
      `${base()}/skus/${skuId}/supersessions`,
    );
  },

  createSkuSupersession(
    skuId: string,
    input: CatalogSkuSupersessionCreateInput,
  ) {
    return fetcher.post<CatalogSkuSupersessionDTO>(
      `${base()}/skus/${skuId}/supersessions`,
      input,
    );
  },

  deleteSkuSupersession(id: string) {
    return fetcher.delete<{ ok: boolean }>(`${base()}/sku-supersessions/${id}`);
  },

  listSkuAlternates(skuId: string) {
    return fetcher.get<CatalogSkuAlternateDTO[]>(
      `${base()}/skus/${skuId}/alternates`,
    );
  },

  createSkuAlternate(
    skuId: string,
    input: Omit<CatalogSkuAlternateCreateInput, "catalogSkuId">,
  ) {
    return fetcher.post<CatalogSkuAlternateDTO>(
      `${base()}/skus/${skuId}/alternates`,
      input,
    );
  },

  deleteSkuAlternate(id: string) {
    return fetcher.delete<{ ok: boolean }>(`${base()}/sku-alternates/${id}`);
  },

  listSkuSpecs(skuId: string) {
    return fetcher.get<CatalogSkuSpecDTO[]>(`${base()}/skus/${skuId}/specs`);
  },

  createSkuSpec(
    skuId: string,
    input: Omit<CatalogSkuSpecCreateInput, "catalogSkuId">,
  ) {
    return fetcher.post<CatalogSkuSpecDTO>(
      `${base()}/skus/${skuId}/specs`,
      input,
    );
  },

  deleteSkuSpec(id: string) {
    return fetcher.delete<{ ok: boolean }>(`${base()}/sku-specs/${id}`);
  },

  listEpcGroups(configurationId: string) {
    return fetcher.get<EpcGroupDTO[]>(
      `${base()}/vehicle-configurations/${configurationId}/epc-groups`,
    );
  },

  createEpcGroup(input: EpcGroupCreateInput) {
    return fetcher.post<EpcGroupDTO>(`${base()}/epc-groups`, input);
  },

  updateEpcGroup(id: string, input: EpcGroupUpdateInput) {
    return fetcher.put<EpcGroupDTO>(`${base()}/epc-groups/${id}`, input);
  },

  deleteEpcGroup(id: string) {
    return fetcher.delete<{ ok: boolean }>(`${base()}/epc-groups/${id}`);
  },

  listEpcDiagrams(groupId: string) {
    return fetcher.get<EpcDiagramDTO[]>(
      `${base()}/epc-groups/${groupId}/diagrams`,
    );
  },

  createEpcDiagram(input: EpcDiagramCreateInput) {
    return fetcher.post<EpcDiagramDTO>(`${base()}/epc-diagrams`, input);
  },

  updateEpcDiagram(id: string, input: EpcDiagramUpdateInput) {
    return fetcher.put<EpcDiagramDTO>(`${base()}/epc-diagrams/${id}`, input);
  },

  deleteEpcDiagram(id: string) {
    return fetcher.delete<{ ok: boolean }>(`${base()}/epc-diagrams/${id}`);
  },

  listEpcDiagramParts(diagramId: string) {
    return fetcher.get<EpcDiagramPartDTO[]>(
      `${base()}/epc-diagrams/${diagramId}/parts`,
    );
  },

  createEpcDiagramPart(input: EpcDiagramPartCreateInput) {
    return fetcher.post<EpcDiagramPartDTO>(
      `${base()}/epc-diagram-parts`,
      input,
    );
  },

  updateEpcDiagramPart(id: string, input: EpcDiagramPartUpdateInput) {
    return fetcher.put<EpcDiagramPartDTO>(
      `${base()}/epc-diagram-parts/${id}`,
      input,
    );
  },

  deleteEpcDiagramPart(id: string) {
    return fetcher.delete<{ ok: boolean }>(`${base()}/epc-diagram-parts/${id}`);
  },

  listEpcFitmentProducts(
    configurationId: string,
    query: { limit?: number; offset?: number; search?: string },
  ) {
    const params = new URLSearchParams();
    params.set("limit", String(query.limit ?? 20));
    params.set("offset", String(query.offset ?? 0));
    if (query.search?.trim()) params.set("search", query.search.trim());
    return fetcher.get<CatalogProductListResult & { data: EpcFitmentProductListItem[] }>(
      `${base()}/vehicle-configurations/${configurationId}/epc-fitment-products?${params.toString()}`,
    );
  },

  listEpcFitmentSkus(configurationId: string, productId: string) {
    return fetcher.get<EpcFitmentSkuListItem[]>(
      `${base()}/vehicle-configurations/${configurationId}/epc-fitment-products/${productId}/skus`,
    );
  },

  listPendingFitmentSkus(params: { limit?: number; offset?: number }) {
    const url = new URL(`${base()}/skus/pending-fitment`);
    url.searchParams.set("limit", String(params.limit ?? 20));
    url.searchParams.set("offset", String(params.offset ?? 0));
    return fetcher.get<PendingFitmentSkusResult>(url.toString());
  },

  rejectPendingProvision(catalogSkuId: string) {
    return fetcher.post<{ ok: true }>(
      `${base()}/skus/${catalogSkuId}/reject-provision`,
      {},
    );
  },

  listCatalogCandidates(params: { limit?: number; offset?: number }) {
    const url = new URL(`${base()}/products/candidates`);
    url.searchParams.set("limit", String(params.limit ?? 20));
    url.searchParams.set("offset", String(params.offset ?? 0));
    return fetcher.get<CatalogCandidatesResult>(url.toString());
  },

  approveCatalogCandidate(catalogProductId: string) {
    return fetcher.post<{ ok: true }>(
      `${base()}/products/candidates/${catalogProductId}/approve`,
      {},
    );
  },

  rejectCatalogCandidate(catalogProductId: string) {
    return fetcher.post<{ ok: true }>(
      `${base()}/products/candidates/${catalogProductId}/reject`,
      {},
    );
  },

  async downloadVehicleImportTemplate(format: "xlsx" | "csv" = "xlsx") {
    const url = `${base()}/vehicles/import/template?format=${format}`;
    const res = await fetch(url, { credentials: "include" });
    if (!res.ok) {
      throw new Error("download_failed");
    }
    const blob = await res.blob();
    const filename =
      format === "csv"
        ? "vehicle-catalog-import-template.csv"
        : "vehicle-catalog-import-template.xlsx";
    const objectUrl = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = objectUrl;
    anchor.download = filename;
    anchor.click();
    URL.revokeObjectURL(objectUrl);
  },

  previewVehicleImport(file: File) {
    const body = new FormData();
    body.append("file", file);
    return fetcher.postForm<VehicleImportResult>(
      `${base()}/vehicles/import/preview`,
      body,
    );
  },

  commitVehicleImport(file: File) {
    const body = new FormData();
    body.append("file", file);
    return fetcher.postForm<VehicleImportResult>(
      `${base()}/vehicles/import?confirm=true`,
      body,
    );
  },
};

export type PendingFitmentSkuRow = {
  id: string;
  catalogProductId: string;
  productName: string;
  oemPartNo: string | null;
  fitmentStatus: "pending" | "complete";
  updatedAt: string;
};

export type PendingFitmentSkusResult = {
  data: PendingFitmentSkuRow[];
  meta: { total: number; limit: number; offset: number };
};

export type CatalogCandidateRow = {
  catalogProductId: string;
  catalogSkuId: string;
  productName: string;
  oemPartNo: string | null;
  type: string;
  proposedByShopId: string | null;
  proposedByShopName: string | null;
  updatedAt: string;
};

export type CatalogCandidatesResult = {
  data: CatalogCandidateRow[];
  meta: { total: number; limit: number; offset: number };
};
